import Router from 'next/router';

import { useSubSideNavStore } from '~/components/v2/store/features/workspace/useSubSideNavStore';
import { SIDEBAR_SIZE } from '~/constants/localStorageKeys';
import { CURRENT_WORKSPACE_NAV_WIDTH } from '~/constants/sideNav';
import { isSubSideNavOpen } from '~/utils/isSubSideNavOpen';

export const SIDEBAR_WIDTH_VAR = '--sidebar-width';
/** Inner nav width. `--sidebar-width` tweens to 0px on collapse; this var stays at the stored size so labels clip instead of reflowing. */
export const SIDEBAR_CONTENT_WIDTH_VAR = '--sidebar-content-width';

export const pathnameFromRouterUrl = (url: string) => url.split('#')[0]?.split('?')[0] ?? url;

const canAnimateSubNavWidth = () => typeof window !== 'undefined' && !('Cypress' in window);

const readStoredSidebarWidth = (): number => {
  if (typeof window === 'undefined') {
    return CURRENT_WORKSPACE_NAV_WIDTH;
  }

  const stored = window.localStorage.getItem(SIDEBAR_SIZE);

  if (stored) {
    const parsed = parseInt(stored, 10);

    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return CURRENT_WORKSPACE_NAV_WIDTH;
};

export const setSubSideNavContentWidth = (widthPx: number) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.style.setProperty(SIDEBAR_CONTENT_WIDTH_VAR, `${widthPx}px`);
};

export const setSubSideNavWidth = (open: boolean, isResizing = false) => {
  if (typeof document === 'undefined') {
    return;
  }

  const htmlElement = document.documentElement;
  const storedWidthPx = readStoredSidebarWidth();
  const nextWidth = open ? `${storedWidthPx}px` : '0px';
  const currentWidth = htmlElement.style.getPropertyValue(SIDEBAR_WIDTH_VAR).trim();

  setSubSideNavContentWidth(storedWidthPx);

  htmlElement.toggleAttribute('data-sub-side-nav-animated', canAnimateSubNavWidth() && !isResizing);

  if (currentWidth === nextWidth) {
    return;
  }

  htmlElement.style.setProperty(SIDEBAR_WIDTH_VAR, nextWidth);
};

const applyUrl = (url: string) => {
  const { open, isResizing } = useSubSideNavStore.getState();
  setSubSideNavWidth(isSubSideNavOpen(pathnameFromRouterUrl(url), open), isResizing);
};

const onRouteChangeStart = (url: string) => {
  applyUrl(url);
};

const onRouteChangeComplete = (url: string) => {
  applyUrl(url);
};

const getRouterAsPath = () => {
  try {
    return Router.asPath;
  } catch {
    return '';
  }
};

const onRouteChangeError = () => {
  applyUrl(getRouterAsPath());
};

/**
 * Next.js remounts `getLayout` on every page. Animate `--sidebar-width` on
 * `documentElement` (not a layout node) so the tween survives remount and is not
 * cancelled when `data-ui2026` is briefly removed.
 */
export const subscribeSubSideNavWidthSync = () => {
  if (typeof window === 'undefined' || !Router.events?.on) {
    return;
  }

  Router.events.off('routeChangeStart', onRouteChangeStart);
  Router.events.on('routeChangeStart', onRouteChangeStart);
  Router.events.off('routeChangeComplete', onRouteChangeComplete);
  Router.events.on('routeChangeComplete', onRouteChangeComplete);
  Router.events.off('routeChangeError', onRouteChangeError);
  Router.events.on('routeChangeError', onRouteChangeError);
};
